import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2, MapPin } from "lucide-react";

import { formatUyMinor } from "@/lib/formatUyMinor";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { AddressMapPreview } from "@/components/shared/AddressMapPreview";
import { geocodeAddress, type GeocodeResult } from "@/utils/geocodeAddress";

export interface QuoteDeliveryDestinationCardProps {
  zone: string | null;
  feeMinor: number;
  adminArea: string | null;
  /** The consumer's full delivery address, snapshotted at intake. */
  formattedAddress?: string | null;
  /** How to find the door: "portón negro, timbre 3". */
  courierNotes?: string | null;
  /**
   * The point, when the server already knows it (spec b73fdd78) — captured
   * from the consumer's own Places pick, or geocoded once server-side. When
   * these are present NOTHING here calls Google.
   */
  latitude?: number | null;
  longitude?: number | null;
  /**
   * Injected so the render spec can drive the four controls without a network
   * or a Google key. Production passes nothing and gets the real geocoder.
   */
  geocode?: (address: string, apiKey: string) => Promise<GeocodeResult>;
}

/**
 * Where this job's document has to be delivered (spec 8c2aab49, then f462e7d6).
 *
 * THE FULL ADDRESS NOW, where this was the department and nothing finer. The
 * old rule was argued from two premises that have both stopped holding: that
 * the street address is contact information, and that "the winner gets what
 * they need to post the parcel through fulfilment instead". There is no
 * fulfilment leg — the professional drives there themselves — and a trip
 * cannot be priced or planned from a department. Montevideo is one shipping
 * tier and three hours of traffic.
 *
 * The risk the old rule named is real and is ACCEPTED rather than argued away:
 * a bidder who can read the address can propose arranging the job
 * off-platform. It is the same trade spec 300908fc makes in handing the
 * professional the consumer's billing identity — the information is needed to
 * do the job.
 *
 * WHAT STAYS WITHHELD IS THE CHANNEL: no phone, no email, here or anywhere
 * else a professional looks. Contact-moderation still polices free text.
 */
export function QuoteDeliveryDestinationCard({
  zone,
  feeMinor,
  adminArea,
  formattedAddress,
  courierNotes,
  latitude,
  longitude,
  geocode,
}: QuoteDeliveryDestinationCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  /*
    THE SERVED POINT IS THE STARTING STATE, not something fetched. A request
    whose point the server knows needs no lookup at all — the browser's
    geocoder is now only the fallback for rows that predate the snapshot or
    whose address Google has not been asked about yet (spec b73fdd78, C-4).
  */
  const servedPoint =
    latitude != null && longitude != null
      ? ({ status: "located", lat: latitude, lng: longitude } as const)
      : null;
  const [state, setState] = useState<
    { status: "idle" } | { status: "loading" } | GeocodeResult
  >(servedPoint ?? { status: "idle" });
  /*
    ONE LOOKUP PER ADDRESS, FOR THE LIFE OF THIS MOUNT (control C-2). A
    geocode is a billed request and this card renders for every professional
    looking at every physical-delivery RFQ, so collapsing and re-expanding
    must not re-bill. Keyed on the address itself rather than a bare boolean,
    so a card that is handed a different request re-resolves.
  */
  const resolvedFor = useRef<string | null>(null);
  const mapRegionId = `quote-destination-map-${zone ?? "x"}`;
  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  // Nothing to say on a digital job, and an empty card saying so is worse than
  // no card at all. Keyed off the DEPARTMENT rather than the full address: a
  // request created before the address step carries one and not the other, and
  // the department is still worth showing on its own.
  if (!adminArea?.trim()) return null;
  const street = formattedAddress?.trim() ?? "";
  const notes = courierNotes?.trim() ?? "";
  const canShowMap = street !== "";

  /*
    GEOCODED ON EXPAND, NEVER ON RENDER (control C-2). An effect keyed on the
    address would bill a lookup for every professional who merely opened the
    page — which is most of them, since they are deciding whether to bid at
    all. Collapsed-by-default is what bounds the cost, so the request has to
    hang off the interaction rather than off the mount.
  */
  const handleToggle = async (): Promise<void> => {
    const next = !open;
    setOpen(next);
    // Served points need no lookup, ever.
    if (!next || servedPoint != null || resolvedFor.current === street) return;
    resolvedFor.current = street;
    setState({ status: "loading" });
    const lookup = geocode ?? geocodeAddress;
    setState(await lookup(street, mapsApiKey));
  };

  return (
    <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
      <CardContent className="pt-6">
        <div className="flex gap-2.5">
          <MapPin
            className="mt-0.5 h-4 w-4 shrink-0 text-primary"
            aria-hidden
          />
          <div className="min-w-0 max-w-[68ch] space-y-1">
            <h3 className="text-[0.9375rem] font-semibold leading-6 text-foreground">
              {t("notary.requestDetail.quoteDestinationTitle")}
            </h3>
            {/* The snapshot as the server stored it, not re-formatted: it is
                what the job was quoted and will be delivered against. */}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {street === "" ? adminArea : street}
            </p>
            {notes === "" ? null : (
              <p className="text-xs leading-relaxed text-muted-foreground">
                {notes}
              </p>
            )}
            {/*
              THE FEE GETS A BOX, because it is the one line on this card the
              professional has to ACT on — it is a suggestion they may change,
              sitting among facts they cannot. As a muted footnote under the
              address it read like small print about the address rather than
              a number about their own price.
            */}
            {zone && feeMinor > 0 ? (
              <p className="!mt-2.5 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-xs text-foreground dark:bg-muted/25">
                {t("notary.requestDetail.quoteDestinationShipping", {
                  amount: formatUyMinor(feeMinor),
                })}
              </p>
            ) : null}

            {/*
              THE MAP, BEHIND A TOGGLE AND ONLY WHEN THERE IS A STREET TO FIND.

              Offered on `street`, not on adminArea: a request made before the
              address step carries a department, and geocoding "Canelones"
              returns the centroid of the whole department — a pin that LOOKS
              specific while pointing somewhere nobody chose. That is worse
              than no map, so there is no toggle (control C-1).
            */}
            {canShowMap ? (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => void handleToggle()}
                  aria-expanded={open}
                  aria-controls={mapRegionId}
                  className="flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 transition-colors hover:underline"
                >
                  {open
                    ? t("notary.requestDetail.quoteDestinationHideMap")
                    : t("notary.requestDetail.quoteDestinationShowMap")}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform duration-150",
                      open && "rotate-180",
                    )}
                    aria-hidden
                  />
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {open ? (
          /*
            THE MAP SPANS THE CARD, outside the prose column. That column caps
            at 68ch because that is a readable MEASURE for an address and a
            fee — a constraint about text, which a map has no business
            inheriting. Inside it the map rendered ~300px wide in a card three
            times that, which is the width a map is least useful at.
          */
          <div id={mapRegionId} className="mt-3 pl-[26px]">
            {state.status === "loading" ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                {t("notary.requestDetail.quoteDestinationLocating")}
              </p>
            ) : state.status === "located" ? (
              /*
                        The caption says APPROXIMATE on purpose. It is not
                        hedging: this pin is a geocoder's reading of a line of
                        free text, and on a rural camino or a lote it can land
                        a block or a field away. The address above is the one
                        the bid is priced against, and the caption must not
                        let the map quietly outrank it (residue RES-1).
                      */
              <AddressMapPreview
                lat={state.lat}
                lng={state.lng}
                caption={t("notary.requestDetail.quoteDestinationMapCaption")}
                mapsApiKey={mapsApiKey}
              />
            ) : (
              /*
                        NO MAP FRAME ON A FAILED LOOKUP (C-3). The address
                        above is untouched and remains the authority — it is
                        what the bid is priced against — and the two answers
                        are kept apart: "the geocoder does not know this
                        address" is a fact worth printing, "our lookup broke"
                        is ours to own.
                      */
              <p className="text-xs text-muted-foreground">
                {state.status === "not_found"
                  ? t("notary.requestDetail.quoteDestinationNotFound")
                  : t("notary.requestDetail.quoteDestinationMapUnavailable")}
              </p>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
